import { useState, useEffect } from "react";
import { supabase } from "./supabaseClient";



const fontLink = document.createElement("link");
fontLink.rel = "stylesheet";
fontLink.href = "https://fonts.googleapis.com/css2?family=Bebas+Neue&family=DM+Sans:wght@400;500;600;700;800&family=DM+Mono:wght@400;500;600&display=swap";
document.head.appendChild(fontLink);

const CONDITIONS  = ["Unknown","Good","Fair","Poor","Distressed"];
const MOTIVATIONS = ["Hot — Needs to sell now","Warm — Open to offer","Cold — Just exploring","Not interested"];
const SITUATIONS  = ["Inherited property","Pre-foreclosure","Divorce","Tax delinquent","Tired landlord","Job loss / financial","Relocation","Estate sale","Other"];

export default function IntakeForm() {
  const params  = new URLSearchParams(window.location.search);
  const teamId   = params.get("team") || "";
  const ownerId  = params.get("owner") || "";
  const callerParam = params.get("caller") || "";

  const [form, setForm] = useState({
    callerName:   callerParam,
    sellerName:   "",
    phone:        "",
    address:      "",
    city:         "",
    state:        "",
    zip:          "",
    askingPrice:  "",
    condition:    "Unknown",
    motivation:   "Warm — Open to offer",
    situation:    "",
    equity:       "",
    taxLien:      false,
    vacant:       false,
    notes:        "",
  });

  const [step, setStep]       = useState(1); // 1=caller, 2=seller, 3=property, 4=situation, 5=success
  const [submitting, setSubmitting] = useState(false);
  const [error, setError]     = useState("");

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const submit = async () => {
    if (!form.sellerName.trim() || !form.phone.trim()) { setError("Seller name and phone are required."); return; }
    setSubmitting(true); setError("");
    try {
      const hotFlag = form.motivation.startsWith("Hot");
      const addr = [form.address, form.city, form.state, form.zip].filter(Boolean).join(", ");
      const lead = {
        id:         Date.now(),
        user_id:    ownerId || teamId || null,
        stage:      hotFlag ? "talking" : "new",
        name:       form.sellerName.trim(),
        phone:      form.phone.trim(),
        address:    addr || "Unknown",
        sqft:       null,
        equity:     parseInt(form.equity) || 0,
        tax_lien:   form.taxLien,
        vacant:     form.vacant,
        score:      hotFlag ? 80 : 50,
        hot:        hotFlag,
        notes:      [
          form.situation ? `Situation: ${form.situation}` : "",
          form.condition !== "Unknown" ? `Condition: ${form.condition}` : "",
          form.askingPrice ? `Asking: $${form.askingPrice}` : "",
          form.motivation ? `Motivation: ${form.motivation}` : "",
          form.notes ? `Notes: ${form.notes}` : "",
          `Submitted by: ${form.callerName || "Cold Caller"}`,
        ].filter(Boolean).join(" | "),
        arv:              null,
        comps:            [],
        rehab_tier:       null,
        condition_report: null,
        sms_history:      [],
        follow_ups:       [],
        last_contact:     null,
      };
      const { error: dbErr } = await supabase.from("leads").insert(lead);
      if (dbErr) { setError("Failed to submit: " + dbErr.message); setSubmitting(false); return; }
      setStep(5);
    } catch(e) {
      setError("Error: " + e.message);
    }
    setSubmitting(false);
  };

  const resetForm = () => {
    setForm(f => ({ ...f, sellerName:"", phone:"", address:"", city:"", state:"", zip:"", askingPrice:"", condition:"Unknown", motivation:"Warm — Open to offer", situation:"", equity:"", taxLien:false, vacant:false, notes:"" }));
    setStep(2);
    setError("");
  };

  const fs = {
    width:"100%", boxSizing:"border-box",
    background:"#111113", border:"1px solid #1C1C1F",
    borderRadius:10, padding:"14px 16px",
    color:"#E8E6E1", fontSize:15,
    fontFamily:"'DM Sans',sans-serif", outline:"none",
    WebkitAppearance:"none",
  };

  const ls = {
    display:"block", fontSize:11,
    fontFamily:"'DM Mono',monospace",
    color:"#6B6B70", letterSpacing:1, marginBottom:8,
  };

  const btn = (primary=true) => ({
    width:"100%", border:"none", borderRadius:10,
    padding:"16px", fontSize:15, fontWeight:700,
    cursor:"pointer", fontFamily:"'DM Sans',sans-serif",
    background: primary ? "linear-gradient(135deg,#C9A96E,#A8834A)" : "#1C1C1F",
    color: primary ? "#0F0F11" : "#8A8885",
    marginBottom: primary ? 0 : 10,
  });

  const progressPct = Math.round(((step-1)/4)*100);

  return (
    <div style={{ minHeight:"100vh", background:"#0A0A0B", display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"flex-start", padding:"24px 16px", fontFamily:"'DM Sans',sans-serif" }}>

      {/* Header */}
      <div style={{ width:"100%", maxWidth:480, marginBottom:24 }}>
        <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between" }}>
          <div>
            <div style={{ fontFamily:"'Bebas Neue',display", fontSize:26, letterSpacing:3, color:"#E8E6E1", lineHeight:1 }}>
              WHOLESALE<span style={{ color:"#C9A96E" }}>IQ</span>
            </div>
            <div style={{ fontSize:11, color:"#2A2A2E", fontFamily:"'DM Mono',monospace", letterSpacing:2, marginTop:2 }}>LEAD INTAKE</div>
          </div>
          {step < 5 && (
            <div style={{ textAlign:"right" }}>
              <div style={{ fontSize:11, color:"#45454A", fontFamily:"'DM Mono',monospace", marginBottom:4 }}>STEP {step} OF 4</div>
              <div style={{ width:80, height:4, background:"#1C1C1F", borderRadius:2, overflow:"hidden" }}>
                <div style={{ width:`${progressPct}%`, height:"100%", background:"#C9A96E", borderRadius:2, transition:"width .3s" }}/>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Card */}
      <div style={{ width:"100%", maxWidth:480, background:"#111113", border:"1px solid #1C1C1F", borderRadius:16, padding:24, boxShadow:"0 24px 60px #0008" }}>

        {/* ── STEP 1: Caller name ── */}
        {step===1&&(
          <div>
            <div style={{ fontSize:9, letterSpacing:3, color:"#C9A96E", fontFamily:"'DM Mono',monospace", marginBottom:6 }}>WHO ARE YOU?</div>
            <div style={{ fontSize:22, fontWeight:800, color:"#E8E6E1", marginBottom:24 }}>Enter your name</div>
            <div style={{ marginBottom:20 }}>
              <label style={ls}>YOUR NAME</label>
              <input value={form.callerName} onChange={e=>set("callerName",e.target.value)}
                onKeyDown={e=>e.key==="Enter"&&form.callerName.trim()&&setStep(2)}
                placeholder="e.g. Maria, John, Cold Call Team"
                style={fs} autoFocus/>
            </div>
            <button onClick={()=>{if(form.callerName.trim())setStep(2);else setError("Please enter your name.");}} style={btn()}>
              NEXT →
            </button>
            {error&&<div style={{marginTop:12,color:"#C06149",fontSize:13,textAlign:"center"}}>{error}</div>}
          </div>
        )}

        {/* ── STEP 2: Seller info ── */}
        {step===2&&(
          <div>
            <div style={{ fontSize:9, letterSpacing:3, color:"#C9A96E", fontFamily:"'DM Mono',monospace", marginBottom:6 }}>SELLER INFO</div>
            <div style={{ fontSize:22, fontWeight:800, color:"#E8E6E1", marginBottom:24 }}>Who did you reach?</div>
            <div style={{ marginBottom:16 }}>
              <label style={ls}>SELLER NAME *</label>
              <input value={form.sellerName} onChange={e=>set("sellerName",e.target.value)} placeholder="John Smith" style={fs} autoFocus/>
            </div>
            <div style={{ marginBottom:20 }}>
              <label style={ls}>PHONE NUMBER *</label>
              <input value={form.phone} onChange={e=>set("phone",e.target.value)} placeholder="(713) 555-1234" style={{...fs,fontSize:18,letterSpacing:1}} type="tel"/>
            </div>
            <button onClick={()=>{if(form.sellerName.trim()&&form.phone.trim()){setError("");setStep(3);}else setError("Name and phone are required.");}} style={btn()}>NEXT →</button>
            <button onClick={()=>setStep(1)} style={{...btn(false),marginTop:10}}>← BACK</button>
            {error&&<div style={{marginTop:12,color:"#C06149",fontSize:13,textAlign:"center"}}>{error}</div>}
          </div>
        )}

        {/* ── STEP 3: Property ── */}
        {step===3&&(
          <div>
            <div style={{ fontSize:9, letterSpacing:3, color:"#C9A96E", fontFamily:"'DM Mono',monospace", marginBottom:6 }}>PROPERTY</div>
            <div style={{ fontSize:22, fontWeight:800, color:"#E8E6E1", marginBottom:24 }}>Tell us about the property</div>
            <div style={{ marginBottom:14 }}>
              <label style={ls}>STREET ADDRESS</label>
              <input value={form.address} onChange={e=>set("address",e.target.value)} placeholder="4821 Oleander Dr" style={fs}/>
            </div>
            <div style={{ display:"grid", gridTemplateColumns:"2fr 1fr 1fr", gap:10, marginBottom:14 }}>
              <div>
                <label style={ls}>CITY</label>
                <input value={form.city} onChange={e=>set("city",e.target.value)} placeholder="Houston" style={fs}/>
              </div>
              <div>
                <label style={ls}>STATE</label>
                <input value={form.state} onChange={e=>set("state",e.target.value)} placeholder="TX" style={fs}/>
              </div>
              <div>
                <label style={ls}>ZIP</label>
                <input value={form.zip} onChange={e=>set("zip",e.target.value)} placeholder="77018" style={fs}/>
              </div>
            </div>
            <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:10, marginBottom:14 }}>
              <div>
                <label style={ls}>ASKING PRICE</label>
                <input value={form.askingPrice} onChange={e=>set("askingPrice",e.target.value)} placeholder="$250,000" style={fs} type="text"/>
              </div>
              <div>
                <label style={ls}>EQUITY % (est.)</label>
                <input value={form.equity} onChange={e=>set("equity",e.target.value)} placeholder="60" style={fs} type="number"/>
              </div>
            </div>
            <div style={{ marginBottom:14 }}>
              <label style={ls}>CONDITION</label>
              <div style={{ display:"flex", gap:8, flexWrap:"wrap" }}>
                {CONDITIONS.map(c=>(
                  <button key={c} onClick={()=>set("condition",c)} style={{ padding:"10px 14px", borderRadius:8, cursor:"pointer", fontSize:13, background:form.condition===c?"#C9A96E22":"#111113", border:`1px solid ${form.condition===c?"#C9A96E":"#2A2A2E"}`, color:form.condition===c?"#C9A96E":"#6B6B70" }}>{c}</button>
                ))}
              </div>
            </div>
            <div style={{ display:"flex", gap:16, marginBottom:20 }}>
              <label style={{ display:"flex", alignItems:"center", gap:8, cursor:"pointer" }}>
                <input type="checkbox" checked={form.taxLien} onChange={e=>set("taxLien",e.target.checked)} style={{ width:18, height:18, accentColor:"#C06149" }}/>
                <span style={{ fontSize:14, color:"#C4C2BD" }}>Tax Lien</span>
              </label>
              <label style={{ display:"flex", alignItems:"center", gap:8, cursor:"pointer" }}>
                <input type="checkbox" checked={form.vacant} onChange={e=>set("vacant",e.target.checked)} style={{ width:18, height:18, accentColor:"#C9935A" }}/>
                <span style={{ fontSize:14, color:"#C4C2BD" }}>Vacant</span>
              </label>
            </div>
            <button onClick={()=>setStep(4)} style={btn()}>NEXT →</button>
            <button onClick={()=>setStep(2)} style={{...btn(false),marginTop:10}}>← BACK</button>
          </div>
        )}

        {/* ── STEP 4: Situation & notes ── */}
        {step===4&&(
          <div>
            <div style={{ fontSize:9, letterSpacing:3, color:"#C9A96E", fontFamily:"'DM Mono',monospace", marginBottom:6 }}>MOTIVATION</div>
            <div style={{ fontSize:22, fontWeight:800, color:"#E8E6E1", marginBottom:24 }}>Why are they selling?</div>
            <div style={{ marginBottom:16 }}>
              <label style={ls}>MOTIVATION LEVEL</label>
              <div style={{ display:"flex", flexDirection:"column", gap:8 }}>
                {MOTIVATIONS.map(m=>{
                  const isHot = m.startsWith("Hot");
                  const isWarm = m.startsWith("Warm");
                  const color = isHot?"#C06149":isWarm?"#C9935A":m.startsWith("Cold")?"#C9A96E":"#45454A";
                  return(
                    <button key={m} onClick={()=>set("motivation",m)} style={{ padding:"14px 16px", borderRadius:10, cursor:"pointer", fontSize:14, textAlign:"left", background:form.motivation===m?color+"22":"#111113", border:`1px solid ${form.motivation===m?color:"#1C1C1F"}`, color:form.motivation===m?color:"#6B6B70", fontWeight:form.motivation===m?700:400 }}>
                      {m}
                    </button>
                  );
                })}
              </div>
            </div>
            <div style={{ marginBottom:16 }}>
              <label style={ls}>SITUATION (optional)</label>
              <div style={{ display:"flex", flexWrap:"wrap", gap:8 }}>
                {SITUATIONS.map(s=>(
                  <button key={s} onClick={()=>set("situation",form.situation===s?"":s)} style={{ padding:"8px 12px", borderRadius:8, cursor:"pointer", fontSize:12, background:form.situation===s?"#9B7ED422":"#111113", border:`1px solid ${form.situation===s?"#9B7ED4":"#2A2A2E"}`, color:form.situation===s?"#9B7ED4":"#6B6B70" }}>{s}</button>
                ))}
              </div>
            </div>
            <div style={{ marginBottom:20 }}>
              <label style={ls}>ADDITIONAL NOTES</label>
              <textarea value={form.notes} onChange={e=>set("notes",e.target.value)}
                placeholder="Any extra details from the call... timeline, repairs needed, family situation, etc."
                rows={4} style={{...fs,resize:"none",lineHeight:1.6}}/>
            </div>
            {error&&<div style={{marginBottom:12,color:"#C06149",fontSize:13,textAlign:"center"}}>{error}</div>}
            <button onClick={submit} disabled={submitting} style={{ ...btn(), background:submitting?"#1C1C1F":"linear-gradient(135deg,#C9A96E,#A8834A)", color:submitting?"#45454A":"#0F0F11" }}>
              {submitting?"SUBMITTING...":"✓ SUBMIT LEAD"}
            </button>
            <button onClick={()=>setStep(3)} style={{...btn(false),marginTop:10}}>← BACK</button>
          </div>
        )}

        {/* ── STEP 5: Success ── */}
        {step===5&&(
          <div style={{ textAlign:"center", padding:"20px 0" }}>
            <div style={{ fontSize:56, marginBottom:16 }}>✅</div>
            <div style={{ fontSize:9, letterSpacing:3, color:"#5A8A60", fontFamily:"'DM Mono',monospace", marginBottom:8 }}>LEAD SUBMITTED</div>
            <div style={{ fontSize:24, fontWeight:800, color:"#E8E6E1", marginBottom:8 }}>{form.sellerName}</div>
            <div style={{ fontSize:14, color:"#6B6B70", marginBottom:6 }}>{[form.address,form.city,form.state].filter(Boolean).join(", ")}</div>
            <div style={{ fontSize:14, color:"#6B6B70", marginBottom:24 }}>
              {form.motivation.startsWith("Hot") ? (
                <span style={{ color:"#C06149", fontWeight:700 }}>🔥 Hot lead — flagged for immediate follow-up</span>
              ) : (
                <span>Added to pipeline as New Lead</span>
              )}
            </div>
            <div style={{ background:"#111113", border:"1px solid #1C1C1F", borderRadius:10, padding:"14px 16px", marginBottom:24, textAlign:"left" }}>
              <div style={{ fontSize:11, color:"#45454A", fontFamily:"'DM Mono',monospace", marginBottom:8 }}>LEAD SUMMARY</div>
              {[
                ["Phone",    form.phone],
                ["Asking",   form.askingPrice ? "$"+form.askingPrice : "Not given"],
                ["Condition",form.condition],
                ["Situation",form.situation||"Not specified"],
                ["Submitted by", form.callerName||"Cold Caller"],
              ].map(([k,v])=>(
                <div key={k} style={{ display:"flex", justifyContent:"space-between", padding:"6px 0", borderBottom:"1px solid #1C1C1F", fontSize:13 }}>
                  <span style={{ color:"#45454A" }}>{k}</span>
                  <span style={{ color:"#C4C2BD", fontWeight:600 }}>{v}</span>
                </div>
              ))}
            </div>
            <button onClick={resetForm} style={btn()}>+ SUBMIT ANOTHER LEAD</button>
          </div>
        )}
      </div>

      {/* Footer */}
      {step < 5 && (
        <div style={{ marginTop:20, fontSize:11, color:"#1C1C1F", fontFamily:"'DM Mono',monospace", textAlign:"center" }}>
          POWERED BY WHOLESALEIQ
        </div>
      )}
    </div>
  );
}
